import { PreviousContract } from './../../models/swagger/bff/previousContract';
import { CatalogService, PreviousInsurancesResponse } from '@core/services/api/http/catalog.service';
import { Injectable, Injector } from '@angular/core';
import { environment } from '@environments/environment';
import { Store } from '@ngrx/store';
import { AppState } from '@store/app.state';
import { Subject, tap } from 'rxjs';
import {
  CustomerStatusDigitalData,
  DigitalData,
  EnvironmentDigitalData,
  GenderDigitalData,
  UserStatusDigitalData,
  YesNoDigitalData,
} from '../../models/digitalData.model';
import { InitFlow, PolicyholderData } from '@core/models/swagger';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { Patterns } from '@global/enums';
import { Flow } from '@global/routePaths';
import SHA256 from 'crypto-js/sha256';
import { findParameterResultById } from '@global/utils';
import { QbItaParameterResult } from '@fwmovilidad/qb-home-ita-components-lib';
import { MultiQuoteResponse } from '@core/models/swagger/bff';

dayjs.extend(customParseFormat);

declare global {
  interface Window {
    dataLayer?: unknown[];
  }
}
declare let digitalData: DigitalData;
declare let stepView: (stepName: string) => void;
declare let popupView: (popupName: string) => void;

@Injectable({
  providedIn: 'root',
})
export class AnalyticsService {
  private readonly destroy$: Subject<boolean> = new Subject<boolean>();
  private readonly store: Store<AppState>;

  previousInsurances: PreviousInsurancesResponse;

  constructor(
    private readonly injector: Injector,
    private readonly catalogService: CatalogService
  ) {
    this.store = this.injector.get(Store);
    this.initializeDigitalData();
    this.setEnvironment(
      environment.production ? EnvironmentDigitalData.PRODUCTION : EnvironmentDigitalData.DEVELOPMENT
    );
  }

  private initializeDigitalData(): void {
    digitalData = {
      environment: '',
      dl_version: '1.0',
      application: 'web',
      site_version: '1.0',
      page_info: {
        brand: 'verti',
        page_type: 'process',
        country: 'de',
        channel: 'digital business',
        url: '',
        referrer: '',
        language: 'de',
      },
      user: {
        client_user_id: '',
        user_status: '',
        client_product_type: '',
        email: '',
        phone: '',
        first_name: '',
        last_name: '',
        gender: '',
        address: {
          street: '',
          house_number: '',
          city: '',
          zip_code: '',
          country: 'de',
        },
      },
      page_error: {
        error_type: '',
        error_message: '',
      },
      process: {
        process_id: 'qb private liability',
        full_operation_id: '',
        registration_operation_id: '',
        transaction_id: '',
        flow_id: '',
        step_number: '',
        step_description: '',
        step_common: '',
        step_basic: '',
        step_version: '',
        progress_id: '',
        purchase_price: '',
        modality: '',
        product_name: 'private liability',
      },
      custom: {
        insurance_start_date: '',
        zip_code: '',
        policyholder_type: '',
        policyholder_birthday: '',
        customer_id: '',
        previous_insurance_brand: '',
        insurance_modalities_offered: '',
        insurance_prices_offered: '',
        quoting_id: '',
        claim_report_365_days: '',
        previous_insurance_duration: '',
        profession: '',
        phone: '',
        payment_method: '',
        customer_status: '',
        prev_insurer_canceled: '',
        voucher: '',
        test_user: 'false',
      },
    };
  }

  sendStepView(stepName: string): void {
    this.setReferrer(digitalData?.page_info?.url);
    this.setUrl(window.location.href);

    try {
      if (environment.environmentName !== 'PRO') {
        console.log(
          JSON.stringify(
            {
              method: 'stepView',
              stepName,
              digitalData: this.getDigitalData(),
            },
            null,
            2
          )
        );
      }
      stepView(this.encodeValue(stepName));
    } catch (e) {}
  }

  sendPopupView(popupName: string): void {
    try {
      if (environment.environmentName !== 'PRO') {
        console.log(
          JSON.stringify({
            method: 'popupView',
            popupName,
            digitalData: this.getDigitalData(),
          })
        );
      }
      popupView(this.encodeValue(popupName));
    } catch (e) {}
  }

  sendFormError(payload: { form_field: string; error_message: string }): void {
    const event = {
      event: 'form_field_error',
      form_field: payload.form_field,
      error_message: payload.error_message,
    };
    try {
      if (environment.environmentName !== 'PRO') {
        console.log('FormError', event);
      }
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push(event);
    } catch {}
  }

  /**
   * Normalizes NFD
   * Removes diacritics except 'ñ' (ES6)
   * Removes invalid characters
   * Removes spaces at the start and the end
   * Transforms into lowercase
   */
  private encodeValue(value?: string): string {
    if (!value && typeof value !== 'string') {
      return '';
    }
    return value
      .replace(/ß/g, 'ss')
      .normalize('NFD')
      .replace(/([^n\u0300-\u036f]|n(?!\u0303(?![\u0300-\u036f])))[\u0300-\u036f]+/gi, '$1')
      .replace(/[^a-zA-Z 0-9.-|]+/g, '')
      .trim()
      .toLowerCase();
  }

  private humanizeValue(value?: string): string {
    if (!value || typeof value !== 'string') {
      return '';
    }
    return value
      .replace(/_/g, ' ')
      .replace(/([a-z])([A-Z])/g, '$1 $2')
      .toLowerCase();
  }

  getDigitalData(): DigitalData {
    return digitalData;
  }

  setEnvironment(environment: EnvironmentDigitalData): void {
    digitalData.environment = this.encodeValue(environment) as EnvironmentDigitalData;
  }

  setDlVersion(dlVersion: string): void {
    digitalData.dl_version = this.encodeValue(dlVersion);
  }

  setApplication(application: string): void {
    digitalData.application = this.encodeValue(application);
  }

  setSiteVersion(siteVersion: string): void {
    digitalData.site_version = this.encodeValue(siteVersion);
  }

  /**
   * Page Info
   */
  setBrandPageInfo(brand: string): void {
    digitalData.page_info.brand = this.encodeValue(brand);
  }

  setPageType(pageType: string): void {
    digitalData.page_info.page_type = this.encodeValue(pageType);
  }

  setCountry(country: string): void {
    digitalData.page_info.country = this.encodeValue(country);
  }

  setChannel(channel: string): void {
    digitalData.page_info.channel = this.encodeValue(channel);
  }

  setUrl(url: string): void {
    digitalData.page_info.url = url;
  }

  setReferrer(referrer: string): void {
    digitalData.page_info.referrer = referrer;
  }

  setPromotionalCode(referrer: string): void {
    digitalData.custom.voucher = referrer;
  }

  setLanguage(language: string): void {
    digitalData.page_info.language = this.encodeValue(language);
  }

  /**
   * User
   */
  setClientUserId(clientUserId: string): void {
    digitalData.user.client_user_id = this.encodeValue(clientUserId);
  }

  setUserStatus(userStatus: string): void {
    digitalData.user.user_status = this.encodeValue(userStatus) as UserStatusDigitalData;
  }

  setClientProductType(clientProductType: string): void {
    digitalData.user.client_product_type = this.encodeValue(clientProductType);
  }

  setEmail(policyholder: PolicyholderData): void {
    const email = policyholder?.contact?.email;

    if (!email) {
      digitalData.user.email = '';
      return;
    }
    digitalData.user.email = this.encodeValue(email);
  }

  setUserPhone(policyholder: PolicyholderData): void {
    const phone = policyholder?.contact?.phone;

    if (!phone) {
      digitalData.user.phone = '';
      return;
    }
    digitalData.user.phone = `+49${phone}`;
  }

  setFirstName(policyholder: PolicyholderData): void {
    const firstName = policyholder?.name;

    if (!firstName) {
      digitalData.user.first_name = '';
      return;
    }
    digitalData.user.first_name = firstName.trim().toLowerCase();
  }

  setLastName(policyholder: PolicyholderData): void {
    const lastName = policyholder?.surname;

    if (!lastName) {
      digitalData.user.last_name = '';
      return;
    }
    digitalData.user.last_name = lastName.trim().toLowerCase();
  }

  setGender(policyholder: PolicyholderData): void {
    const gender = policyholder?.gender?.parameterId;

    if (!gender) {
      digitalData.user.gender = '';
      return;
    }
    digitalData.user.gender = GenderDigitalData[gender];
  }

  setStreet(policyholder: PolicyholderData): void {
    const street = policyholder?.address?.street;

    if (!street) {
      digitalData.user.address.street = '';
      return;
    }
    digitalData.user.address.street = this.encodeValue(street);
  }

  setHouseNumber(policyholder: PolicyholderData): void {
    const houseNumber = policyholder?.address?.houseNumber;

    if (!houseNumber) {
      digitalData.user.address.house_number = '';
      return;
    }
    digitalData.user.address.house_number = this.encodeValue(houseNumber);
  }

  setCity(policyholder: PolicyholderData): void {
    const city = policyholder?.address?.city;

    if (!city) {
      digitalData.user.address.city = '';
      return;
    }
    digitalData.user.address.city = this.encodeValue(city);
  }

  setUserZipCode(policyholder: PolicyholderData): void {
    const zipCode = policyholder?.address?.postCode;

    if (!zipCode) {
      digitalData.user.address.zip_code = '';
      return;
    }
    digitalData.user.address.zip_code = this.encodeValue(zipCode);
  }

  setUserCountry(country: string): void {
    if (!country) {
      digitalData.user.address.country = '';
      return;
    }
    digitalData.user.address.country = this.encodeValue(country);
  }

  /**
   * Page Error
   */
  setErrorType(errorType: string): void {
    digitalData.page_error.error_type = this.encodeValue(errorType);
  }

  setErrorMessage(errorMessage: string): void {
    digitalData.page_error.error_message = this.encodeValue(errorMessage);
  }

  /**
   * Process
   */
  setProcessId(processId: string): void {
    digitalData.process.process_id = this.encodeValue(processId);
  }

  setFullOperationId(fullOperationId: string): void {
    digitalData.process.full_operation_id = this.encodeValue(fullOperationId);
  }

  setRegistrationOperationId(registrationOperationId: string): void {
    digitalData.process.registration_operation_id = this.encodeValue(registrationOperationId);
  }

  setTransactionId(transactionId: string): void {
    digitalData.process.transaction_id = transactionId;
  }

  setFlowId(flowId: Flow): void {
    const formattedFlowId = 'long-quote';

    digitalData.process.flow_id = formattedFlowId;
  }

  setStepNumber(stepNumber: string): void {
    digitalData.process.step_number = this.encodeValue(`step${stepNumber}`);
  }

  setStepDescription(stepDescription: string): void {
    digitalData.process.step_description = this.encodeValue(stepDescription);
  }

  setStepCommon(stepCommon: string): void {
    if (!stepCommon) {
      digitalData.process.step_common = '';
      return;
    }
    digitalData.process.step_common = this.encodeValue(`step${stepCommon}`);
  }

  setStepBasic(stepBasic: string): void {
    if (!stepBasic) {
      digitalData.process.step_basic = '';
      return;
    }
    digitalData.process.step_basic = this.encodeValue(stepBasic);
  }

  setStepVersion(stepVersion: string): void {
    digitalData.process.step_version = this.encodeValue(stepVersion);
  }

  setProgressId(progressId: string): void {
    digitalData.process.progress_id = this.encodeValue(progressId);
  }

  setPurchasePrice(purchasePrice: string): void {
    if (!purchasePrice) {
      digitalData.process.purchase_price = '';
      return;
    }

    digitalData.process.purchase_price = purchasePrice;
  }

  setModalityProcess(modality: string): void {
    digitalData.process.modality = this.encodeValue(modality);
  }

  setProductName(productName: string): void {
    digitalData.process.product_name = this.encodeValue(productName);
  }

  /**
   * Custom
   */

  setInsuranceStartDate(insuranceStartDate: string): void {
    if (!insuranceStartDate) {
      digitalData.custom.insurance_start_date = '';
      return;
    }

    const formattedInsuranceStartDate = dayjs(insuranceStartDate, Patterns.DATE_DASH_YYYYMMDD).format(
      Patterns.DATE_WITHOUT_SEPARATION_DDMMYYYY
    );

    digitalData.custom.insurance_start_date = this.encodeValue(formattedInsuranceStartDate);
  }

  setZipCode(zipCode: string): void {
    digitalData.custom.zip_code = this.encodeValue(zipCode);
  }

  setPolicyholderType(policyholderType: string): void {
    digitalData.custom.policyholder_type = this.encodeValue(this.humanizeValue(policyholderType));
  }

  setPolicyholderBirthday(policyholderBirthday: string): void {
    if (!policyholderBirthday || !dayjs(policyholderBirthday).isValid()) {
      digitalData.custom.policyholder_birthday = '';
      return;
    }
    digitalData.custom.policyholder_birthday = this.encodeValue(
      dayjs(policyholderBirthday, Patterns.DATE_DASH_YYYYMMDD).format(Patterns.DATE_WITHOUT_SEPARATION_DDMMYYYY)
    );
  }

  setCustomerId(customerId: string): void {
    if (!customerId) {
      digitalData.custom.customer_id = '';
      return;
    }

    const emailHash = SHA256(customerId).toString();

    digitalData.custom.customer_id = this.encodeValue(emailHash);
  }

  setPreviousInsuranceBrand(previousInsurer: string, carInsurers: QbItaParameterResult[]): void {
    if (!previousInsurer) {
      digitalData.custom.previous_insurance_brand = '';
      return;
    }
    let previousInsurerFormatted: string;

    this.catalogService
      .getPreviousInsurances()
      .pipe(
        tap(previousInsurances => {
          previousInsurerFormatted =
            findParameterResultById(carInsurers, previousInsurer)?.parameterName ??
            findParameterResultById(previousInsurances, previousInsurer)?.parameterName;
        })
      )
      .subscribe();

    digitalData.custom.previous_insurance_brand = this.encodeValue(previousInsurerFormatted);
  }

  setInsuranceModalitiesOffered(productLines: InitFlow): void {
    let modalities: string[] = [];
    let parametres = productLines?.parameters.filter(item => item.name.startsWith('cover_details_')) || [];
    for (let productLine of parametres) {
      modalities.unshift(productLine?.value?.productLine?.value ?? '');
    }
    digitalData.custom.insurance_modalities_offered = this.encodeValue(modalities.join(';'));
  }

  setInsurancePricesOffered(productLines: MultiQuoteResponse): void {
    let pricesAux: string[] = [];
    const prices = productLines?.regularPrices || [];
    for (let productLine of prices) {
      for (let price of productLine?.salesProduct?.prices) {
        pricesAux.unshift(price?.amount ?? '');
      }
    }
    digitalData.custom.insurance_prices_offered = this.encodeValue(pricesAux.join(';'));
  }

  setQuotingId(quotingId: string): void {
    digitalData.custom.quoting_id = this.encodeValue(quotingId);
  }

  setClaimReport365Days(claimReport365Days: string): void {
    if (!claimReport365Days) {
      digitalData.custom.claim_report_365_days = '';
      return;
    }

    digitalData.custom.claim_report_365_days = claimReport365Days === 'Ja' ? YesNoDigitalData.YES : YesNoDigitalData.NO;
  }

  setPreviousInsuranceDuration(previousInsuranceDuration: string): void {
    if (!previousInsuranceDuration) {
      digitalData.custom.previous_insurance_duration = '';
      return;
    }
    digitalData.custom.previous_insurance_duration = this.encodeValue(this.humanizeValue(previousInsuranceDuration));
  }

  setProfession(profession: string): void {
    digitalData.custom.profession = this.encodeValue(this.humanizeValue(profession));
  }

  setPhone(phone: string): void {
    if (!phone) {
      digitalData.custom.phone = '';
      return;
    }

    const phoneHash = SHA256(phone).toString();

    digitalData.custom.phone = phoneHash;
  }

  setPaymentMethod(paymentMethod: string): void {
    digitalData.custom.payment_method = this.encodeValue(paymentMethod);
  }

  setCustomerStatus(situation: boolean | undefined, previousInsurer?: string | undefined | null): void {
    if (situation === undefined) {
      digitalData.custom.customer_status = '';
      return;
    }

    if (situation) {
      digitalData.custom.customer_status = CustomerStatusDigitalData.EXISTING_CUSTOMER;
    } else {
      digitalData.custom.customer_status = CustomerStatusDigitalData.NEW_CUSTOMER;
    }
    // let previousInsurerFormatted: PreviousInsurancesResponse;

    // this.catalogService
    //   .getPreviousInsurances()
    //   .pipe(
    //     tap(previousInsurances => {
    //       previousInsurerFormatted = previousInsurances;
    //       if (previousInsurer === previousInsurerFormatted[0].parameterId) {
    //         digitalData.custom.customer_status = CustomerStatusDigitalData.EXISTING_CUSTOMER;
    //         return;
    //       }
    //     })
    //   )
    //   .subscribe();

    // if (previousInsurer === 'other') {
    //   digitalData.custom.customer_status = CustomerStatusDigitalData.NEW_CUSTOMER;
    //   return;
    // }
  }

  setPrevInsurerCanceled(prevInsurerCanceled: boolean): void {
    if (prevInsurerCanceled == undefined) {
      digitalData.custom.prev_insurer_canceled = '';
      return;
    }
    digitalData.custom.prev_insurer_canceled = prevInsurerCanceled ? YesNoDigitalData.YES : YesNoDigitalData.NO;
  }

  setTestUser(isTestUser: boolean): void {
    digitalData.custom.test_user = isTestUser ? 'true' : 'false';
  }
}
